import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import { useHistory } from 'react-router-dom'
import PersonOutlineOutlinedIcon from 'components/material-symbol/icons/PersonOutline'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import CalendarTodayOutlinedIcon from 'components/material-symbol/icons/CalendarToday'
import CheckIcon from 'components/material-symbol/icons/Check'
import AutoAwesomeIcon from 'components/material-symbol/icons/AutoAwesome'
import DefaultDashboard from './default-dashboard'
import DashboardHeader from './dashboard-header'
import get from 'lodash/get'
import ChoiceStartModal from 'components/choice-start-modal'
import LibraryBooksOutlinedIcon from 'components/material-symbol/icons/LibraryBooks'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import SchoolOutlinedIcon from 'components/material-symbol/icons/School'
import ArrowForwardIcon from 'components/material-symbol/icons/ArrowForward'
import { TonalButton } from 'components/empty-page-state'
import OnboardingProgressSection, {
  getOnboardingProgress,
} from 'components/onboarding-progress'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import {
  SettingsLinkRow,
  SettingsSection,
} from 'pages/pga-coach/settings/settings-components'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { getMarketplaceListingReasons } from 'lib/coach-profile/marketplace-listing'
import PerformanceBenchmarks from './performance-benchmarks'
import useDashboardStyles from './styles'

const useStyles = makeStyles(() => ({
  skipButton: {
    alignSelf: 'flex-start',
  },
}))

const ScheduleDialog = ({ dialogOpen, closeDialog, listItems }) => {
  const history = useHistory()

  return (
    <ChoiceStartModal
      open={dialogOpen}
      onClose={closeDialog}
      title="Add to your Schedule"
      subtitle="What type of offering would you like to add to your calendar?"
      columns={2}
      options={listItems.map(block => ({
        id: block.link,
        icon: block.icon,
        title: block.title,
        description: block.description,
        onClick: () => {
          closeDialog()
          history.push(block.link)
        },
      }))}
    />
  )
}

const buildListItems = coachOnboardingProgress => {
  const isComplete = key => coachOnboardingProgress[key] !== null

  return [
    {
      step: 'create-coach-profile',
      icon: <PersonOutlineOutlinedIcon />,
      primary: 'Create a PGA Coach Profile',
      secondary:
        'Get discovered on PGA.com and let golfers book with you. You can hide your profile anytime.',
      link: '/pga-coach/onboarding/profile/new',
      completed: isComplete('coachProfileCreated'),
    },
    {
      step: 'add-facility',
      icon: <RoomOutlinedIcon />,
      primary: 'Set your Location(s)',
      secondary:
        'Add where you coach so golfers can find you. Mark private clubs as private.',
      link: '/pga-coach/onboarding/facilities/new',
      completed: isComplete('facilityAdded'),
    },
    {
      step: 'add-availability',
      icon: <EventAvailableOutlinedIcon />,
      primary: 'Set your Working Hours',
      secondary:
        "Set when you're available before adding lesson types and offerings.",
      link: '/pga-coach/onboarding/availability',
      completed: isComplete('availabilityAdded'),
    },
    {
      step: 'add-schedule',
      icon: <CalendarTodayOutlinedIcon />,
      primary: 'Add to your Schedule',
      secondary:
        'Create private lessons and group programs that fit your coaching style.',
      completed: isComplete('schedulableCreated'),
      scheduleListItems: [
        {
          title: 'Booking Types',
          description: (
            <>
              Booking Types are offerings for <strong>individuals</strong>. Some
              examples: private coaching sessions, club fittings, swing
              evaluations, and lesson packs.
            </>
          ),
          icon: LibraryBooksOutlinedIcon,
          link: '/pga-coach/onboarding/bookings/booking-types/new',
        },
        {
          title: 'Group Events',
          description: (
            <>
              Group Events are offerings for <strong>groups</strong>, like after
              school clinics, Get Golf Ready, supervised practice, and boot
              camps.
            </>
          ),
          icon: CelebrationOutlinedIcon,
          link: '/pga-coach/onboarding/bookings/events/new',
        },
      ],
    },
  ]
}

const advancedListItems = [
  {
    icon: <AutoAwesomeIcon />,
    primary: 'ADM',
    secondary:
      'Access American Development Model resources to support long-term athlete development.',
    link:
      'https://vt.lightspeedvt.com/share/redirect.cfm?signInURL=https://webservices.lightspeedvt.net/SAML/lsvt/login.aspx?h=auth0.pga.com&dest=https://vt.lightspeedvt.com/reports/certification/',
  },
  {
    icon: <SchoolOutlinedIcon />,
    primary: 'Training',
    secondary:
      'Explore coaching courses, videos, and resources in the Training Center.',
    link:
      'https://vt.lightspeedvt.com/share/redirect.cfm?signInURL=https://webservices.lightspeedvt.net/SAML/lsvt/login.aspx?h=auth0.pga.com&dest=https://vt.lightspeedvt.com/trainingCenter/',
  },
]

const OnboardingStepRow = ({ item, settingsStyles }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const rowIcon = item.completed ? <CheckIcon /> : item.icon

  const sharedProps = {
    icon: rowIcon,
    iconWrapClassName: item.completed
      ? settingsStyles.rowIconCompleted
      : settingsStyles.rowIconTinted,
    title: item.primary,
    description: item.completed ? undefined : item.secondary,
    completed: item.completed,
    badge: item.completed ? 'Done' : undefined,
    classes: settingsStyles,
    'data-testid': item.step,
  }

  if (item.completed) {
    return <SettingsLinkRow {...sharedProps} />
  }

  if (item.link) {
    return <SettingsLinkRow {...sharedProps} to={item.link} />
  }

  return (
    <>
      <SettingsLinkRow {...sharedProps} onClick={() => setDialogOpen(true)} />
      <ScheduleDialog
        listItems={item.scheduleListItems}
        dialogOpen={dialogOpen}
        closeDialog={() => setDialogOpen(false)}
      />
    </>
  )
}

const OnboardingDashboard = ({ coachOnboardingProgress, onSkip }) => {
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const listItems = buildListItems(coachOnboardingProgress)

  return (
    <Box className={settingsStyles.contentContainer}>
      <OnboardingProgressSection
        coachOnboardingProgress={coachOnboardingProgress}
      />

      <Box className={settingsStyles.section}>
        <Box className={settingsStyles.card}>
          <Box className={settingsStyles.cardBody}>
            {listItems.map(item => (
              <OnboardingStepRow
                key={item.step}
                item={item}
                settingsStyles={settingsStyles}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <SettingsSection eyebrow="Advanced setup" classes={settingsStyles}>
        {advancedListItems.map(item => (
          <SettingsLinkRow
            key={item.primary}
            href={item.link}
            icon={item.icon}
            iconWrapClassName={settingsStyles.rowIconTinted}
            title={item.primary}
            description={item.secondary}
            classes={settingsStyles}
          />
        ))}
      </SettingsSection>

      <TonalButton
        className={classes.skipButton}
        onClick={onSkip}
        startIcon={<ArrowForwardIcon fontSize="small" />}
      >
        Skip to Dashboard
      </TonalButton>
    </Box>
  )
}

const Dashboard = ({ data }) => {
  const dashboardStyles = useDashboardStyles()
  const [
    isCoachBenchmarksEnabled,
    isMarketplaceListingBannerEnabled,
  ] = useFeatureFlags([
    flags.FLAG_FEAT_COACH_BENCHMARKS,
    flags.FLAG_FEAT_MARKETPLACE_LISTING_BANNER,
  ])
  const coach = get(data, 'currentUser.attributes')
  const { coachOnboardingProgress } = coach
  const slug = coach?.coachProfile?.slug
  const [skipped, setSkipped] = useState(false)
  const { completedCount, totalSteps } = getOnboardingProgress(
    coachOnboardingProgress,
  )
  const showDefaultDashboard = skipped || completedCount === totalSteps
  const marketplaceListingReasons = isMarketplaceListingBannerEnabled
    ? getMarketplaceListingReasons(coach)
    : []

  return (
    <>
      <DashboardHeader
        slug={slug}
        variant={showDefaultDashboard ? 'default' : 'onboarding'}
      />
      {showDefaultDashboard ? (
        <DefaultDashboard
          hasGroupEventsOrLessonTypes={
            coachOnboardingProgress.schedulableCreated
          }
          slug={slug}
          marketplaceListingReasons={marketplaceListingReasons}
        />
      ) : (
        <OnboardingDashboard
          coachOnboardingProgress={coachOnboardingProgress}
          onSkip={() => setSkipped(true)}
        />
      )}
      {isCoachBenchmarksEnabled ? (
        <Box className={dashboardStyles.container}>
          <PerformanceBenchmarks />
        </Box>
      ) : null}
    </>
  )
}

export default Dashboard
